<template>
  <PageHeader
    title="Targets & Results"
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.dealership', 2) },
      { label: 'Targets & Results' }
    ]"
  >
    <template #right>
      <div class="flex gap-2 items-center flex-col lg:flex-row md:gap-4">
        <div class="flex gap-2">
          <Select
            v-model="dealershipsSelected"
            :options="dealershipsList"
            mode="multiple"
            searchable
            placeholder="Select a Dealership"
            option-group-label="manufacturer"
            option-group-children="dealerships"
            class="min-w-80"
          />
        </div>
        <div class="flex gap-2">
          <DatePicker
            v-model="dateRange"
            selection-mode="range"
            class="min-w-60"
            show-presets
            :presets="dateRangePresets"
            :months-visible="2"
            :close-on-select="false"
          />
        </div>
        <Button
          label="Update"
          :is-loading="dataIsLoading"
          :disabled="!dealershipsSelected.length"
          @click="onDateRangeChange"
        />
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="computedData"
      export-filename="Dealerships Targets & Results"
      removable-sort
      sort-field="dealershipName"
      :sort-order="-1"
      :rows="50"
      data-key="dealershipName"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-reports-dealerships-targets-results"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['dealershipName']"
      scrollable
      :loading="dataIsLoading"
    >
      <template #empty>
        <Empty
          v-if="!dataIsLoading && !dealershipsSelected.length"
          title="Select a dealership"
          icon="car-building"
        />
        <div v-else-if="!dataIsLoading">No records found</div>
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex w-full justify-between">
            <div class="flex gap-4">
              <span>
                <Input
                  v-model="filters['global'].value"
                  :placeholder="$t('ui.common.search', 1)"
                  icon="search"
                />
              </span>
              <Button
                :label="$t('ui.actions.clear', 1)"
                severity="secondary"
                type="button"
                outlined
                icon="filter-slash"
                @click="clearFilters()"
              />
            </div>
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="dealershipName"
        header="Dealership"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.dealershipName }}
        </template>
      </Column>

      <Column
        field="attemptedDifference"
        header="Attempted"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip :severity="data.attempted >= data.attemptedTarget ? 'success' : 'danger'">
            {{ data.attempted }} / {{ data.attemptedTarget }}
          </Chip>
        </template>
      </Column>

      <Column
        field="attempted"
        header="Attempted Total"
        hidden
      />
      <Column
        field="attemptedTarget"
        header="Attempted Target"
        hidden
      />

      <Column
        field="answeredDifference"
        header="Answered"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip :severity="data.answered >= data.answeredTarget ? 'success' : 'danger'">
            {{ data.answered }} / {{ data.answeredTarget }}
          </Chip>
        </template>
      </Column>

      <Column
        field="answered"
        header="Answered Total"
        hidden
      />
      <Column
        field="answeredTarget"
        header="Answered Target"
        hidden
      />

      <Column
        field="bookedDifference"
        header="Booked"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip :severity="data.booked >= data.bookedTarget ? 'success' : 'danger'">
            {{ data.booked }} / {{ data.bookedTarget }}
          </Chip>
        </template>
      </Column>

      <Column
        field="booked"
        header="Booked Total"
        hidden
      />
      <Column
        field="bookedTarget"
        header="Booked Target"
        hidden
      />

      <Column
        field="occurredDifference"
        header="Occurred"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip :severity="data.occurred >= data.occurredTarget ? 'success' : 'danger'">
            {{ data.occurred }} / {{ data.occurredTarget }}
          </Chip>
        </template>
      </Column>

      <Column
        field="occurred"
        header="Occurred Total"
        hidden
      />
      <Column
        field="occurredTarget"
        header="Occurred Target"
        hidden
      />
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import Card from '@/components/card/Card.vue'
import DatePicker from '@/components/forms/DatePicker.vue'

import useApiRequest from '@/composables/useApiRequest'
import { useUserStore } from '@/stores/UserStore'
import groupDealershipsByManufacturer from '@/utilities/groupDealerships'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from 'primevue/api'
import dayjs from 'dayjs'
import Chip from '@/components/chip/Chip.vue'
import Select from '@/components/forms/Select.vue'
import Empty from '@/components/empty/Empty.vue'

const userStore = useUserStore()
const dealershipsList = ref([])

onMounted(async () => {
  dealershipsList.value = await groupDealershipsByManufacturer(userStore.accessAllowed.dealerships)
})

const dt = ref({ processedData: [] })
const dealershipsSelected = ref([])

const dateRange = ref([dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  dealershipName: { value: null, matchMode: FilterMatchMode.IN }
})

const clearFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipName: { value: null, matchMode: FilterMatchMode.IN }
  }
}

const dealershipStats = ref([])

const apiRequest = useApiRequest()
const dataIsLoading = ref(false)
function fetchData() {
  dataIsLoading.value = true

  apiRequest
    .send({
      endpoint: '/v1/reports/dealerships/targets-and-results',
      params: {
        dealerships: dealershipsSelected.value.join(','),
        start: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
        end: dayjs(dateRange.value[1]).format('YYYY-MM-DD')
      }
    })
    .then(response => {
      dealershipStats.value = response.data
      dataIsLoading.value = false
    })
}

function onDateRangeChange() {
  if (dealershipsSelected.value.length && dateRange.value[0] && dateRange.value[1]) {
    fetchData()
  }
}

const exportCSV = () => {
  dt.value.exportCSV()
}

const computedData = computed(() => {
  const dealershipsData = []
  // Loop through each dealership
  dealershipStats.value.forEach(dealership => {
    const dealershipData = {
      ...dealership,
      attemptedDifference: dealership.attemptedTarget - dealership.attempted,
      answeredDifference: dealership.answeredTarget - dealership.answered,
      bookedDifference: dealership.bookedTarget - dealership.booked,
      occurredDifference: dealership.occurredTarget - dealership.occurred
    }
    dealershipsData.push(dealershipData)
  })
  return dealershipsData
})
</script>

<style></style>
